import { Lab, Panel, PanelItem } from '../../../../types/bloodWork'

/**
 * Transforms a saved lab document into an editable format for the form.
 * Converts numeric values to strings for input fields.
 */
export function transformLabForEdit(copyLab: Lab): Lab {
  return {
    title: copyLab.title,
    archived: copyLab.archived,
    panels: copyLab.panels.map((panel) => ({
      id: panel.id,
      title: panel.title,
      archived: panel.archived,
      requiresProvocationType: panel.requiresProvocationType,
      items: panel.items.map((item) => ({
        id: item.id,
        units: item.units?.map((unit) => ({
          id: unit.id,
          referenceRange: {
            top: unit.referenceRange.top.toString() || '',
            bottom: unit.referenceRange.bottom.toString() || '',
          },
          optimalRange: {
            top: unit.optimalRange?.top.toString() || '',
            bottom: unit.optimalRange?.bottom.toString() || '',
          },
        })),
        title: item.title,
        referenceRange: {
          top: item.referenceRange.top.toString() || '',
          bottom: item.referenceRange.bottom.toString() || '',
        },
        optimalRange: {
          top: item.optimalRange?.top.toString() || '',
          bottom: item.optimalRange?.bottom.toString() || '',
        },
        archived: item.archived,
      })),
    })),
  }
}

/**
 * Transforms form data into the format required for saving to Firestore.
 * Filters out empty units and handles optional fields.
 */
export function transformLabForSave(lab: Lab): Lab {
  return {
    archived: lab.archived || false,
    title: lab.title,
    panels: lab.panels.map((panelInput) => {
      const panel: Panel = {
        id: panelInput.id,
        title: panelInput.title,
        archived: panelInput.archived ?? false,
        items: panelInput.items.map((itemInput) => {
          // Removes added units that are unused
          const activeUnits =
            itemInput.units?.filter((unit) => unit.id !== '') ?? []

          const item: PanelItem = {
            id: itemInput.id,
            title: itemInput.title,
            referenceRange: {
              bottom: itemInput.referenceRange.bottom,
              top: itemInput.referenceRange.top,
            },
            units: activeUnits.map((unit) => ({
              id: unit.id,
              referenceRange: {
                top: unit.referenceRange.top,
                bottom: unit.referenceRange.bottom,
              },
              optimalRange: {
                top: unit.optimalRange?.top ?? '',
                bottom: unit.optimalRange?.bottom ?? '',
              },
            })),
            archived: itemInput.archived ?? false,
          }

          if (itemInput.optimalRange?.bottom && itemInput.optimalRange?.top) {
            item.optimalRange = {
              bottom: itemInput.optimalRange.bottom,
              top: itemInput.optimalRange.top,
            }
          }

          return item
        }),
      }

      if (panelInput.requiresProvocationType) {
        panel.requiresProvocationType = true
      }

      return panel
    }),
  }
}
